/**
 * TextWidget - Editable text display widget with property controls
 *
 * Shows static or dynamic text content. Supports different typography
 * variants (heading, body, caption) and alignment.
 * Click to select, double-click to edit inline.
 *
 * When selected, shows:
 * - Variant selector (heading1/heading2/heading3/body/caption)
 * - Alignment controls (left/center/right)
 * - Voice settings panel
 */

import { memo, useState, useRef, useEffect } from 'react';
import type { TextWidget as TextWidgetType } from '@deep-agents/shared-types';
import type { WidgetComponentProps } from '../types';
import { VoiceConfigEditor } from './VoiceConfigEditor';

interface TextWidgetProps extends Omit<WidgetComponentProps, 'widget'> {
  widget: TextWidgetType;
}

// Typography class mapping
const variantClasses: Record<string, string> = {
  heading1: 'text-2xl font-bold text-gray-900',
  heading2: 'text-xl font-semibold text-gray-900',
  heading3: 'text-lg font-semibold text-gray-800',
  body: 'text-base text-gray-700',
  caption: 'text-sm text-gray-500',
};

// Short display names for the variant selector buttons
const variantLabels: Record<string, string> = {
  heading1: 'H1',
  heading2: 'H2',
  heading3: 'H3',
  body: 'Body',
  caption: 'Caption',
};

const alignClasses: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

export const TextWidgetComponent = memo(function TextWidgetComponent({
  widget,
  isSelected,
  onSelect,
  onUpdate,
}: TextWidgetProps) {
  const { props, voice } = widget;
  const variant = props.variant || 'body';
  const align = props.align || 'left';

  // Local editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(props.content || '');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Sync with props
  useEffect(() => {
    setEditValue(props.content || '');
  }, [props.content]);

  // Handle double-click to edit
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  // Handle click to select
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect();
  };

  // Save changes
  const handleSave = () => {
    setIsEditing(false);
    if (onUpdate && editValue !== props.content) {
      onUpdate({
        ...widget,
        props: {
          ...widget.props,
          content: editValue,
        },
      });
    }
  };

  // Handle keyboard
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      setEditValue(props.content || '');
      setIsEditing(false);
    }
  };

  // Handle variant change
  const handleVariantChange = (newVariant: string) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      props: { ...widget.props, variant: newVariant as TextWidgetType['props']['variant'] },
    });
  };

  // Handle alignment change
  const handleAlignChange = (newAlign: string) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      props: { ...widget.props, align: newAlign as TextWidgetType['props']['align'] },
    });
  };

  // Handle voice config change
  const handleVoiceChange = (voiceConfig: typeof voice) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      voice: voiceConfig,
    });
  };

  return (
    <div
      className={`
        relative px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer
        ${isSelected ? 'ring-2 ring-violet-500 bg-violet-50/50' : 'hover:bg-gray-50'}
      `}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {isEditing ? (
        // Editing mode - show textarea
        <textarea
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`
            nodrag nopan w-full bg-white border border-violet-300 rounded px-2 py-1
            focus:outline-none focus:ring-2 focus:ring-violet-500
            resize-none ${variantClasses[variant]} ${alignClasses[align]}
          `}
          rows={Math.max(1, editValue.split('\n').length)}
          placeholder="Enter text..."
        />
      ) : (
        // Display mode - show text (click to select, double-click to edit)
        <p
          className={`${variantClasses[variant]} ${alignClasses[align]} min-h-[1.5em]`}
          title="Double-click to edit"
        >
          {props.content || 'Double-click to edit'}
        </p>
      )}

      {/* Voice indicator */}
      {voice?.speakable && !isEditing && !isSelected && (
        <div
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center"
          title="Voice can read this"
        >
          <svg
            className="w-2.5 h-2.5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
            />
          </svg>
        </div>
      )}

      {/* ─── PROPERTY CONTROLS (shown when selected and NOT text-editing) ─── */}
      {isSelected && !isEditing && (
        <div
          className="mt-2 pt-2 border-t border-gray-200 space-y-2"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Variant selector */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-medium text-gray-500">Style:</span>
            {Object.keys(variantLabels).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => handleVariantChange(v)}
                className={`
                  nodrag nopan px-2 py-0.5 text-[10px] rounded border transition-colors
                  ${variant === v
                    ? 'bg-violet-100 text-violet-700 border-violet-300'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-violet-200'
                  }
                `}
              >
                {variantLabels[v]}
              </button>
            ))}
          </div>

          {/* Alignment selector */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium text-gray-500">Align:</span>
            {(['left', 'center', 'right'] as const).map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => handleAlignChange(a)}
                className={`
                  nodrag nopan px-2 py-0.5 text-[10px] rounded border transition-colors
                  ${align === a
                    ? 'bg-violet-100 text-violet-700 border-violet-300'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-violet-200'
                  }
                `}
              >
                {/* Simple alignment icons using text */}
                {a === 'left' && '≡ Left'}
                {a === 'center' && '≡ Center'}
                {a === 'right' && '≡ Right'}
              </button>
            ))}
          </div>

          {/* Voice settings (expandable) */}
          <VoiceConfigEditor config={voice} onChange={handleVoiceChange} />

          <p className="text-[9px] text-gray-400">
            Double-click to edit text
          </p>
        </div>
      )}
    </div>
  );
});
